import { useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';

import { Image } from '@vyce/core/src/types';

import { Mode, Phase, Props, QualificationFields } from '../types';
import { MAX_PHOTOS, emptyQualificationFields } from '../config';
import { toJpeg } from '../utils';
import { useQualificationCapture } from './useQualificationCapture';

// Drives the qualification / CSCS capture flow: mode (cscs vs generic), phase
// machine, uploaded photos and the review form. Keeps behaviour out of the
// presentation components. Web entry is always the picker (profile "Add").
export const useQualificationFlow = ({
  isOpen,
  userId = '',
  userLastName,
  onClose,
  reRequestData,
  ...requests
}: Props) => {
  const { extractFromPhotos, saveQualification, extracting, saving } = useQualificationCapture({
    userId,
    userLastName,
    reRequestData,
    ...requests,
  });

  const [mode, setMode] = useState<Mode>('cscs');
  const [phase, setPhase] = useState<Phase>('choose');
  const [photos, setPhotos] = useState<Image[]>([]);

  const methods = useForm<QualificationFields>({ defaultValues: emptyQualificationFields });
  const { reset, watch, handleSubmit } = methods;

  const isCSCS = mode === 'cscs';
  const canSave = !!watch('qualification_name') || !!watch('reference_number');
  const photosLimitReached = photos.length >= (isCSCS ? 2 : MAX_PHOTOS);

  const resetFlow = () => {
    setPhotos([]);
    reset(emptyQualificationFields);
  };

  // Reset everything whenever the dialog is (re)opened so a previous run never leaks.
  useEffect(() => {
    if (isOpen) {
      setMode('cscs');
      setPhase('choose');
      resetFlow();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const runExtraction = async (capturedPhotos: Image[]) => {
    const fields = await extractFromPhotos(capturedPhotos, isCSCS);
    reset(fields);
    setPhase('review');
  };

  const choose = (nextMode: Mode) => {
    setMode(nextMode);
    resetFlow();
    setPhase('intro');
  };

  const addPhoto = async (image: Image) => {
    if (!image.file || photosLimitReached) return;
    const jpeg = await toJpeg(image.file, isCSCS ? 'cscs-photo.jpg' : 'qualification-photo.jpg');
    const next = [...photos, { ...image, file: jpeg }];
    setPhotos(next);
    // CSCS is a fixed two-sided card — once both sides are in, read it automatically.
    if (isCSCS && next.length >= 2) {
      runExtraction(next);
    }
  };

  const removePhoto = (index: number) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const onSave = async (fields: QualificationFields) => {
    const ok = await saveQualification(fields, photos, isCSCS);
    if (ok) {
      setPhase('success');
    }
  };

  return {
    phase,
    isCSCS,
    photos,
    canSave,
    photosLimitReached,
    extracting,
    saving,
    methods,
    choose,
    addPhoto,
    removePhoto,
    startCapture: () => {
      resetFlow();
      setPhase('capture');
    },
    goManual: () => {
      resetFlow();
      setPhase('review');
    },
    goToIntro: () => {
      resetFlow();
      setPhase('intro');
    },
    readPhotos: () => runExtraction(photos),
    submit: handleSubmit(onSave),
    addAnother: () => {
      setMode('qualification');
      resetFlow();
      setPhase('intro');
    },
    close: onClose,
  };
};
